'use client';

import type { CSSProperties, ReactNode } from 'react';
import type { Marked } from 'marked';
import { Cmarkdownlist } from '../Cmarkdownlist/Cmarkdownlist';
import { Cmarkdownlistitem } from '../Cmarkdownlistitem/Cmarkdownlistitem';
import { startsWithHebrew } from './markdown-hebrew';
import type { ParenListItem } from './paren-list-types';

const PAREN_LIST_ITEM_KEY_PREFIX = 'paren-item';
/** `a)` keeps the author's bracket; the numeric marker uses the list dot. */
const PAREN_MARKER_SUFFIX = ')';
const FIRST_ORDINAL = 1;

export interface ParenListBlockProps {
  items: readonly ParenListItem[];
  keyPrefix: string;
  depth: number;
  listStyle?: CSSProperties;
  listItemStyle?: CSSProperties;
  markdownParser: Marked;
  renderItemContent: (input: {
    text: string;
    markdownParser: Marked;
  }) => ReactNode;
}

/** Renders a lettered `a) b) c)` run as a real ordered list. Baked to the
 *  locked Lettered treatment: the author's letters stay the marker. */
export function ParenListBlock({
  items,
  keyPrefix,
  depth,
  listStyle,
  listItemStyle,
  markdownParser,
  renderItemContent,
}: ParenListBlockProps): ReactNode {
  return (
    <Cmarkdownlist ordered depth={depth} style={depth === 0 ? listStyle : undefined}>
      {items.map((item, itemIndex) => (
        <Cmarkdownlistitem
          key={`${keyPrefix}-${PAREN_LIST_ITEM_KEY_PREFIX}-${itemIndex}`}
          ordered
          index={FIRST_ORDINAL + itemIndex}
          marker={`${item.marker}${PAREN_MARKER_SUFFIX}`}
          style={listItemStyle}
          hebrew={startsWithHebrew(item.text)}
        >
          {renderItemContent({ text: item.text, markdownParser })}
        </Cmarkdownlistitem>
      ))}
    </Cmarkdownlist>
  );
}
